import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { queryClient } from '@/queries';
import {
  getSchedulerShiftItemRemover,
  getSchedulerShiftItemUpdater,
  csScheduleQueries,
  getHrSystemConflictItemRemover,
} from '@/queries/resource/schedule';
import {
  DateRangeParams,
  ResetShiftTypeRequestBody,
  SetCSShiftTypeRequestBody,
  SetFixationTypeRequestBody,
  CSRequirementsInputsSettings,
  CSResourceRulesSettings,
  PlanningProcess,
  CSResolveHrSystemConflict,
  CSShift,
  CSTimeGroupRequirementsInputsSettings,
  CSAtHubWeeklyRequirementsInputsSettings,
  CSPeriodResourceRulesSettings,
  CSDeductionShiftRulesSettings,
} from '@/api/apis/resources/schedule';

const handleCSSchedulePlanningQueryUpdate = (startDayOfMonth: Date, planningData: PlanningProcess) => {
  queryClient.invalidateQueries(csScheduleQueries.schedule(startDayOfMonth));
  queryClient.setQueryData(csScheduleQueries.planningProcess(planningData.id).queryKey, planningData);
};

export const useCSScheduleSetShiftTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = csScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: SetCSShiftTypeRequestBody) => api.schedule.cs.setCSShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater(body));

      return prevSnapshot;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater({ ...variables, ...data }));
      queryClient.invalidateQueries(csScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useCSScheduleResetShiftTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = csScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: ResetShiftTypeRequestBody) => api.schedule.cs.resetCSShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemRemover(body));

      return prevSnapshot;
    },
    onSuccess: (_d, variables) => {
      queryClient.invalidateQueries(csScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useCSCreatePlanningMutation = () => {
  return useMutation({
    mutationFn: (startDayOfMonth: Date) => api.schedule.cs.createCSPlanningProcess(startDayOfMonth),
    onSuccess: (data, startDayOfMonth) => {
      handleCSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCSRegeneratePlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.cs.regenerateCSPlanningProcess(processId),
    onSuccess: (data) => {
      handleCSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCSCancelPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.cs.cancelCSPlanningProcess(processId),
    onSuccess: (data) => {
      handleCSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCSPublishPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, scheduleId }: { processId: string; scheduleId: string }) =>
      api.schedule.cs.publishCSPlanningProcess(processId, scheduleId),
    onSuccess: (data) => {
      handleCSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCSCancelGenerationPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, jobId }: { processId: string; jobId: string }) =>
      api.schedule.cs.cancelGenerationCSPlanningProcess(processId, jobId),
    onSuccess: (data) => {
      handleCSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useCSSetScheduleSettingsRulesMutation = () => {
  return useMutation({
    mutationFn: (body: CSResourceRulesSettings) => api.schedule.cs.setCSScheduleRules(body),
    meta: {
      invalidates: [csScheduleQueries.rules().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(csScheduleQueries.rules().queryKey, data);
    },
  });
};

export const useCSSetScheduleSettingsInputsMutation = () => {
  return useMutation({
    mutationFn: (body: CSRequirementsInputsSettings) => api.schedule.cs.setCSScheduleInputs(body),
    meta: {
      invalidates: [csScheduleQueries.inputs().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(csScheduleQueries.inputs().queryKey, data);
    },
  });
};

export const useCSSetSchedulePeriodTimeGroupInputsMutation = () => {
  return useMutation({
    mutationFn: (body: CSTimeGroupRequirementsInputsSettings) =>
      api.schedule.cs.setCSSchedulePeriodTimeGroupInputs(body),
    meta: {
      invalidates: [csScheduleQueries.periodTimeGroupInputs().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(csScheduleQueries.periodTimeGroupInputs().queryKey, data);
    },
  });
};

export const useCSSetSchedulePeriodRulesMutation = () => {
  return useMutation({
    mutationFn: (body: CSPeriodResourceRulesSettings) => api.schedule.cs.setCSSchedulePeriodRules(body),
    meta: {
      invalidates: [csScheduleQueries.periodRules().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(csScheduleQueries.periodRules().queryKey, data);
    },
  });
};

export const useCSSetSchedulePeriodAtHubWeeklyInputsMutation = () => {
  return useMutation({
    mutationFn: (body: CSAtHubWeeklyRequirementsInputsSettings) =>
      api.schedule.cs.setCSSchedulePeriodAtHubWeeklyInputs(body),
    meta: {
      invalidates: [csScheduleQueries.periodAtHubWeeklyInputs().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(csScheduleQueries.periodAtHubWeeklyInputs().queryKey, data);
    },
  });
};

export const useCSSetSchedulePeriodDeductionShiftRulesMutation = () => {
  return useMutation({
    mutationFn: (body: CSDeductionShiftRulesSettings) => api.schedule.cs.setCSSchedulePeriodDeductionShiftRules(body),
    meta: {
      invalidates: [csScheduleQueries.periodDeductionShiftRules().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(csScheduleQueries.periodDeductionShiftRules().queryKey, data);
    },
  });
};

export const useCSScheduleSyncWorkdayMutation = (
  startDayOfMonth: Date,
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  return useMutation({
    mutationFn: (scheduleId: string) => api.schedule.cs.csScheduleRefreshHr(scheduleId),
    onSuccess: (_, scheduleId) => {
      const planningProcessId = isPlanned ? processId : undefined;
      queryClient.invalidateQueries({ queryKey: csScheduleQueries.shifts(dateRange, planningProcessId).queryKey });
      queryClient.invalidateQueries({ queryKey: csScheduleQueries.schedule(startDayOfMonth).queryKey });
      queryClient.invalidateQueries({ queryKey: csScheduleQueries.planningProcess(processId).queryKey });
      queryClient.invalidateQueries({ queryKey: csScheduleQueries.hrSystemConflicts(scheduleId).queryKey });
    },
  });
};

export const useCSHrSystemConflictResolveMutation = (dateRange: DateRangeParams, scheduleId?: string) => {
  return useMutation({
    mutationFn: (payload: CSResolveHrSystemConflict) => api.schedule.cs.resolveCSScheduleHrSystemConflict(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        csScheduleQueries.hrSystemConflicts(scheduleId).queryKey,
        getHrSystemConflictItemRemover(variables.conflictId),
      );
      queryClient.setQueryData(csScheduleQueries.shifts(dateRange).queryKey, getSchedulerShiftItemUpdater(data));
    },
  });
};

export const useCSSetFixationTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = csScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: SetFixationTypeRequestBody) => api.schedule.cs.setCSFixationType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        getSchedulerShiftItemUpdater({ ...body, fixationType: body.fixationType, isEditable: true } as CSShift),
      );
      return prevSnapshot;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(csScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};
